import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Helmet from '../../common/Helmet';
import { WebsiteUrl, PercentageEndPoint } from '../../common/Constants';
import PercentageCalculator from './percentageCalculator';
import { AdsLeftDesktopFirst, AdsAboveCalcMobile } from "../../common/Ads";
import { isMobile } from 'react-device-detect';

const CommonFooter = lazy(() => import('../../common/Footer'));

// Base meta data for the page
const BASE_TITLE = '计算';
const BASE_DESCRIPTION = '了解如何轻松计算百分比。';
const BASE_KEYWORDS = '百分比计算器, 计算百分比, 百分比计算工具';

const numberToMandarin = {
    1: '一',
    2: '二',
    3: '三',
    4: '四',
    5: '五',
    6: '六',
    7: '七',
    8: '八',
    9: '九',
    10: '十',
    11: '十一',
    12: '十二',
    13: '十三',
    14: '十四',
    15: '十五',
    16: '十六',
    17: '十七',
    18: '十八',
    19: '十九',
    20: '二十',
    21: '二十一',
    22: '二十二',
    23: '二十三',
    24: '二十四',
    25: '二十五',
    26: '二十六',
    27: '二十七',
    28: '二十八',
    29: '二十九',
    30: '三十',
    31: '三十一',
    32: '三十二',
    33: '三十三',
    34: '三十四',
    35: '三十五',
    36: '三十六',
    37: '三十七',
    38: '三十八',
    39: '三十九',
    40: '四十',
    41: '四十一',
    42: '四十二',
    43: '四十三',
    44: '四十四',
    45: '四十五',
    46: '四十六',
    47: '四十七',
    48: '四十八',
    49: '四十九',
    50: '五十',
    51: '五十一',
    52: '五十二',
    53: '五十三',
    54: '五十四',
    55: '五十五',
    56: '五十六',
    57: '五十七',
    58: '五十八',
    59: '五十九',
    60: '六十',
    61: '六十一',
    62: '六十二',
    63: '六十三',
    64: '六十四',
    65: '六十五',
    66: '六十六',
    67: '六十七',
    68: '六十八',
    69: '六十九',
    70: '七十',
    71: '七十一',
    72: '七十二',
    73: '七十三',
    74: '七十四',
    75: '七十五',
    76: '七十六',
    77: '七十七',
    78: '七十八',
    79: '七十九',
    80: '八十',
    81: '八十一',
    82: '八十二',
    83: '八十三',
    84: '八十四',
    85: '八十五',
    86: '八十六',
    87: '八十七',
    88: '八十八',
    89: '八十九',
    90: '九十',
    91: '九十一',
    92: '九十二',
    93: '九十三',
    94: '九十四',
    95: '九十五',
    96: '九十六',
    97: '九十七',
    98: '九十八',
    99: '九十九',
};

// Convert number to Mandarin words
function getMandarinNumber(number) {
  if (number <= 0) return '无效数字';
  if (number <= 20) return numberToMandarin[number] || `数字 ${number} 未找到`;

  if (number < 100) {
      const tens = Math.floor(number / 10) * 10;
      const units = number % 10;

      const tensWord = numberToMandarin[tens] || '';
      const unitsWord = numberToMandarin[units] || '';

      if (units === 0) {
          return tensWord;
      } else if (unitsWord) {
          return `${unitsWord} 和 ${tensWord}`;
      } else {
          return `数字 ${number} 未找到`;
      }
  }

  return numberToMandarin[100] || '数字 100 未找到';
}

// Percentage Converter Component
function PercentageConverter() {
  const { percentage } = useParams();
  const validPercentage = Number(percentage);
  const [amount, setAmount] = useState(100);
  const [result, setResult] = useState(amount * (validPercentage / 100));
  const mandarinPercentage = getMandarinNumber(validPercentage);

  useEffect(() => {
      setResult(amount * (validPercentage / 100));
  }, [validPercentage, amount]);

  const TITLE = `${BASE_TITLE} ${validPercentage}% 的金额 - ${validPercentage} 百分比计算器`;
  const TITLE_H2 = `百分比计算器 ${validPercentage}% 的值或金额`;

  const DESCRIPTION = `${BASE_DESCRIPTION} 计算 ${validPercentage}% 的金额 - ${mandarinPercentage} 百分比计算器 - 了解 ${mandarinPercentage} 百分比是多少。这个计算器可以确定 ${validPercentage}% 的值和特定金额或值的百分比。`;

  const KEYWORDS = `${BASE_KEYWORDS}, ${validPercentage}%, ${validPercentage}% 计算, ${validPercentage}% 百分比计算器, 如何计算 ${validPercentage}%, ${validPercentage}% 的金额, 如何计算 ${validPercentage} 百分比, ${validPercentage}% 的值计算, ${validPercentage}% 计算器, 学习 ${validPercentage}%, ${validPercentage}% 的解释, 如何计算 ${validPercentage}%, ${validPercentage} 的百分比, ${validPercentage}% 计算器, 最佳 ${validPercentage}% 计算器, ${validPercentage}% 百分比计算, 百分比计算方法, ${validPercentage}% 计算解释, ${validPercentage}% 的金额计算器, 英文百分比计算器`;
  const lang = "zh";
  const url = WebsiteUrl;
  const OG_URL = `${WebsiteUrl}/${lang}/${PercentageEndPoint}/${validPercentage}`;
  const href = lang === 'ar' ? '/' : `/${lang}/`;

  return (
      <Container>
          <Helmet
              TITLE={TITLE}
              WEBSITE_URL={url}
              DESCRIPTION={DESCRIPTION}
              KEYWORDS={KEYWORDS}
              OG_URL={OG_URL}
              LANG={lang}
          />
          <Row>
              <Col xs={12} md={9} lg={8}>
                  <div className="mainBackground text-white text-center" style={{ height: '55px' }}>
                      <h1 style={{ fontSize: '17px' }}>{TITLE}</h1>
                  </div>
                  <div className="border border-light">
                      <Row>
                          <div>
                              <p>要轻松计算 {validPercentage}% 的任何金额（{mandarinPercentage} 百分比），请按照以下步骤进行：</p>
                              <ul>
                                  <li>首先将百分比转换为分数。 {validPercentage}% 可以表示为分数：{validPercentage}/100。</li>
                                  <li>将原始值乘以这个分数。例如，如果您有一个值为 {amount} 并且想计算 {validPercentage}% 的值，请使用以下公式：
                                      <br />
                                      <strong>百分比 = (原始值 × {validPercentage}/100)</strong>
                                  </li>
                                  <li>一旦执行计算，结果就是原始金额的 {validPercentage}%。</li>
                              </ul>
                              <p>例如，如果原始值是 200 并且您想计算 {validPercentage}% 的值：</p>
                              <ul>
                                  <li>将 {validPercentage}% 转换为分数：{validPercentage}/100。</li>
                                  <li>乘以 200 × {validPercentage}/100 = {(200 * validPercentage) / 100}。</li>
                                  <li>最终结果是 {(200 * validPercentage) / 100}，这表示 200 的 {validPercentage}%。</li>
                              </ul>
                              <p>使用这种方法，您可以轻松计算任何金额的 {validPercentage}%。</p>
                              <p>如果您想快速轻松地执行这些计算，可以使用下面的 <strong>百分比计算器</strong>。这个计算器提供了一个有效的工具来准确确定任何值的百分比。现在就使用它来简化您的计算吧！</p>
                          </div>
                      </Row>
                      <Row className='calcualtorStyle'>
                          <div className='p-3 mb-2 mainBackground text-white' style={{ textAlign: 'center' }}>
                              <h2 style={{ fontSize: '16px' }}>{TITLE_H2}</h2>
                          </div>
                          <hr />
                          <PercentageCalculator percentage={validPercentage} />
                          <hr />
                          <Suspense fallback={<div>加载中...</div>}>
                              <CommonFooter lang={lang} url={OG_URL} textName="引用" />
                          </Suspense>
                          <br />
                          <br />
                      </Row>
                      {isMobile &&
                          <>
                              <hr />
                              <Row>
                                  <AdsAboveCalcMobile />
                                  <br />
                              </Row>
                          </>
                      }
                      <hr />
                      <Row>
                          <div className='alert alert-info'>
                              您还可以使用 <a href={href}>百分比计算器</a> 来计算任何数字的百分比，并确定百分比的增加和减少。
                          </div>
                          <hr />
                      </Row>
                  </div>
              </Col>
              <Col xs={12} md={3} lg={4}>
                  {!isMobile && <AdsLeftDesktopFirst />}
              </Col>
          </Row>
      </Container>
  );
}

export default PercentageConverter;
